import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import sessionStorage from '../utils/sessionStorage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is logged in on app load
  useEffect(() => {
    const checkAuth = async () => {
      const token = sessionStorage.getItem('authToken');
      const storedUser = sessionStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          // Verify token with server
          const response = await authAPI.verifyToken();
          if (response.success) {
            setUser(response.data.user);
            setIsAuthenticated(true);
          } else {
            // Token is invalid, clear storage
            console.log('Token verification failed - clearing storage');
            sessionStorage.removeItem('authToken');
            sessionStorage.removeItem('user');
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          
          // More robust error handling for different scenarios
          const isNetworkError = !error.response || error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR';
          const isServerError = error.response?.status >= 500;
          const isAuthError = error.response?.status === 401;
          
          if (isAuthError && !isNetworkError && !isServerError) {
            // Clear authentication only on clear 401 errors
            console.log('Authentication failed - clearing storage');
            sessionStorage.removeItem('authToken');
            sessionStorage.removeItem('user');
          } else {
            // Keep the token for network/server issues and let the user try again
            console.log('Network/server error during token verification - keeping token');
            setUser(JSON.parse(storedUser));
            setIsAuthenticated(true);
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Listen for cross-tab storage changes
  useEffect(() => {
    const handleStorageChange = (event) => {
      // When another tab changes auth, this tab will be notified
      // We can choose to ignore it or handle it appropriately
      console.log('Cross-tab auth change detected. This tab maintains its own session.');
    };

    window.addEventListener('authStorageChange', handleStorageChange);
    return () => {
      window.removeEventListener('authStorageChange', handleStorageChange);
    };
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      if (response && response.success) {
        const { user, token } = response.data;
        sessionStorage.setItem('authToken', token);
        sessionStorage.setItem('user', JSON.stringify(user));
        sessionStorage.setItem('loginTime', Date.now().toString()); // Store login time
        setUser(user);
        setIsAuthenticated(true);
        return { success: true, user };
      } else {
        return { success: false, message: response?.message || 'Login failed' };
      }
    } catch (error) {
      console.error('AuthContext login error:', error);
      
      // Ensure we always return a proper response object
      if (error && error.response) {
        return { 
          success: false, 
          message: error.response.data?.message || error.response.statusText || 'Login failed'
        };
      } else if (error && error.message) {
        return { 
          success: false, 
          message: error.message
        };
      } else {
        return { 
          success: false, 
          message: 'Login failed. Please try again.'
        };
      }
    }
  };

  const googleLogin = async (credential) => {
    try {
      const response = await authAPI.googleLogin(credential);
      if (response && response.success) {
        const { user, token } = response.data;
        sessionStorage.setItem('authToken', token);
        sessionStorage.setItem('user', JSON.stringify(user));
        sessionStorage.setItem('loginTime', Date.now().toString());
        setUser(user);
        setIsAuthenticated(true);
        return { success: true, user };
      } else {
        return { success: false, message: response?.message || 'Google login failed' };
      }
    } catch (error) {
      console.error('AuthContext googleLogin error:', error);
      if (error && error.response) {
        return { 
          success: false, 
          message: error.response.data?.message || error.response.statusText || 'Google login failed'
        };
      } else if (error && error.message) {
        return { 
          success: false, 
          message: error.message
        };
      } else {
        return { 
          success: false, 
          message: 'Google login failed. Please try again.'
        };
      }
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      if (response.success) {
        return { success: true, message: response.message, data: response.data };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Registration failed' 
      };
    }
  };

  const verifyOTPAndRegister = async (userData) => {
    try {
      const response = await authAPI.verifyOTPAndRegister(userData);
      if (response.success) {
        const { user, token } = response.data;
        sessionStorage.setItem('authToken', token);
        sessionStorage.setItem('user', JSON.stringify(user));
        sessionStorage.setItem('loginTime', Date.now().toString()); // Store login time
        setUser(user);
        setIsAuthenticated(true);
        return { success: true, user };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'OTP verification failed' 
      };
    }
  };

  const sendOTP = async (email, phoneNumber, deliveryMethod) => {
    try {
      const response = await authAPI.sendOTP(email, phoneNumber, deliveryMethod);
      return { success: response.success, message: response.message, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to send OTP' 
      };
    }
  };

  const resendOTP = async (email, phoneNumber = null, deliveryMethod = 'email') => {
    try {
      const response = await authAPI.resendOTP(email, phoneNumber, deliveryMethod);
      return { success: response.success, message: response.message };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to resend OTP' 
      };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('loginTime'); // Clear login time
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await authAPI.updateProfile(profileData);
      if (response.success) {
        const updatedUser = response.data.user;
        sessionStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        return { success: true, user: updatedUser };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Profile update failed' 
      };
    }
  };

  const changePassword = async (passwordData) => {
    try {
      const response = await authAPI.changePassword(passwordData);
      return { success: response.success, message: response.message };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Password change failed' 
      };
    }
  };

  const forgotPasswordRequest = async (identifier) => {
    try {
      const response = await authAPI.forgotPasswordRequest(identifier);
      return { success: response.success, message: response.message, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to initiate password reset' 
      };
    }
  };

  const sendPasswordResetOTP = async (email, phoneNumber, deliveryMethod) => {
    try {
      const response = await authAPI.sendPasswordResetOTP(email, phoneNumber, deliveryMethod);
      return { success: response.success, message: response.message, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to send password reset OTP' 
      };
    }
  };

  const resetPassword = async (email, otp, newPassword) => {
    try {
      const response = await authAPI.resetPassword(email, otp, newPassword);
      return { success: response.success, message: response.message };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Password reset failed' 
      };
    }
  };

  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  const isUser = () => {
    return user && user.role === 'user';
  };

  const isGreeter = () => {
    return user && user.role === 'greeter';
  };

  const isFinanceOperator = () => {
    return user && user.role === 'finance_operator';
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    googleLogin,
    register,
    verifyOTPAndRegister,
    sendOTP,
    resendOTP,
    logout,
    updateProfile,
    changePassword,
    forgotPasswordRequest,
    sendPasswordResetOTP,
    resetPassword,
    isAdmin,
    isUser,
    isGreeter,
    isFinanceOperator
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
